import { createAsyncThunk, createSlice, createSelector } from '@reduxjs/toolkit';
import * as webViewerServersService from '../services/webViewerServers';
import { userConfigsSelectors } from './userConfigs';
import { entitiesAdapter } from './common/adapter';

const initialState = {
  isFetching: false,
  isCreating: false,
  isUpdating: false,
  isRemoving: false,
  isRestoring: false,
  isAssignmentInProcess: false,

  fetchingError: null,
  creatingUpdatingError: null,
  removingRestoringError: null,
  assignmentError: null,

  serverTypes: { 
    personal: 'personal',
    concurrent: 'concurrent',
    unified: 'unified',
  }
};

const sliceName = 'webViewerServers';

export const fetchAllWebViewerServers = createAsyncThunk(
  `${sliceName}/fetchAllWebViewerServers`,
  (args, thunkAPI) => {
    return webViewerServersService.getWebViewerServers().catch((error) => {
      error.clientMessage = "Can't get web viewer servers from server";
      return thunkAPI.rejectWithValue(error);
    });
  }
);

export const fetchById = createAsyncThunk(
  `${sliceName}/fetchById`,
  (args, thunkAPI) => {
    return webViewerServersService.getWebViewerServerById(args.id)
      .catch((error) => {
        error.clientMessage = "Can't get web viewer server from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const create = createAsyncThunk(
  `${sliceName}/create`,
  (args, thunkAPI) => {
    return webViewerServersService.addWebViewerServers(
      args.type, args.connectionCount, args.clientId, args.term, args.linkConnectionCount, args.connectionSharingPolicy, args.linkCount)
      .catch((error) => {
        error.clientMessage = "Can't post web viewer server to server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const update = createAsyncThunk(
  `${sliceName}/update`,
  (args, thunkAPI) => {
    return webViewerServersService.editWebViewerServer(
      args.serverId, args.connectionCount, args.term, args.linkConnectionCount, args.connectionSharingPolicy, args.linkCount)
      .catch((error) => {
        error.clientMessage = "Can't update web viewer server";
        return thunkAPI.rejectWithValue(error);
      });
  }
)

export const assignToClient = createAsyncThunk(
  `${sliceName}/assignToClient`,
  (args, thunkAPI) => {
    return webViewerServersService
      .assignWebViewerServerToClient(args.serverId, args.clientId)
      .catch((error) => {
        error.clientMessage = "Can't get web viewer server from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const fetchByKey = createAsyncThunk(
  `${sliceName}/fetchByKey`,
  (args, thunkAPI) => {
    return webViewerServersService
      .getWebViewerServerByKey(args.serverKey)
      .catch((error) => {
        error.clientMessage = "Can't get web viewer server from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const restore = createAsyncThunk(
  `${sliceName}/restore`,
  (args, thunkAPI) => {
    return webViewerServersService
      .restoreWebViewerServers(args.webViewerServerIds)
      .catch((error) => {
        error.clientMessage = "Can't update web viewer servers on server";
        return thunkAPI.rejectWithValue(error)
      });
  }
)

export const remove = createAsyncThunk(
  `${sliceName}/remove`,
  (args, thunkAPI) => {
    return webViewerServersService
      .removeWebViewerServers(args.webViewerServerIds)
      .catch((error) => {
        error.clientMessage = "Can't update web viewer servers on server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const deactivate = createAsyncThunk(
  `${sliceName}/deactivate`,
  (args, thunkAPI) => {
    return webViewerServersService
      .deactivateWebViewerServer(args.webViewerServerId)
      .catch((error) => {
        error.clientMessage = "Can't deactivate web viewer server on server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const reactivate = createAsyncThunk(
  `${sliceName}/reactivate`,
  (args, thunkAPI) => {
    return webViewerServersService
      .reactivateWebViewerServer(args.webViewerServerId, args.deviceKey)
      .catch((error) => {
        error.clientMessage = "Can't reactivate web viewer server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const addToFavorites = createAsyncThunk(
  `${sliceName}/favoritesAdd`,
  (args) => {
    return webViewerServersService.addWebViewerServerToFavorites(args.id)
  }
);
export const removeFromFavorites = createAsyncThunk(
  `${sliceName}/favoritesRemove`,
  (args) => {
    return webViewerServersService.removeWebViewerServerFromFavorites(args.id)
  }
);

const webViewerServers = createSlice({
  name: sliceName,
  initialState: entitiesAdapter.getInitialState(initialState),
  reducers: {},
  extraReducers: {
    [fetchAllWebViewerServers.fulfilled]: (state, action) => {
      state.isFetching = false;
      entitiesAdapter.setAll(state, action);
    },
    [fetchAllWebViewerServers.pending]: (state) => {
      state.isFetching = true;
      state.fetchingError = null;
    },
    [fetchAllWebViewerServers.rejected]: (state, action) => {
      state.isFetching = false;
      state.fetchingError = action.payload;
    },

    [fetchById.fulfilled]: (state, action) => {
      state.isFetching = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [fetchById.pending]: (state) => {
      state.isFetching = true;
      state.fetchingError = null;
    },
    [fetchById.rejected]: (state, action) => {
      state.isFetching = false;
      state.fetchingError = action.payload;
    },

    [create.fulfilled]: (state, action) => {
      state.isCreating = false;
      entitiesAdapter.addOne(state, action);
    },
    [create.pending]: (state) => {
      state.isCreating = true;
      state.fetchingError = null;
      state.creatingUpdatingError = null;
    },
    [create.rejected]: (state, action) => {
      state.isCreating = false;
      state.creatingUpdatingError = action.payload;
    },

    [update.fulfilled]: (state, action) => {
      state.isUpdating = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [update.pending]: (state) => {
      state.isUpdating = true;
      state.fetchingError = null;
      state.creatingUpdatingError = null;
    },
    [update.rejected]: (state, action) => {
      state.isUpdating = false;
      state.creatingUpdatingError = action.payload;
    },

    [assignToClient.fulfilled]: (state, action) => {
      state.isAssignmentInProcess = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [assignToClient.pending]: (state) => {
      state.isAssignmentInProcess = true;
      state.assignmentError = null;
    },
    [assignToClient.rejected]: (state, action) => {
      state.isAssignmentInProcess = false;
      state.assignmentError = action.payload;
    },

    [fetchByKey.fulfilled]: (state, action) => {
      state.isFetching = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [fetchByKey.pending]: (state) => {
      state.isFetching = true;
      state.fetchingError = null;
    },
    [fetchByKey.rejected]: (state, action) => {
      state.isFetching = false;
      state.fetchingError = action.payload;
    },

    [remove.fulfilled]: (state, action) => {
      state.isRemoving = false;
      entitiesAdapter.upsertMany(state, action);
    },
    [remove.pending]: (state) => {
      state.isRemoving = true;
      state.removingRestoringError = null;
    },
    [remove.rejected]: (state, action) => {
      state.isRemoving = false;
      state.removingRestoringError = action.payload;
    },

    [restore.fulfilled]: (state, action) => {
      state.isRestoring = false;
      entitiesAdapter.upsertMany(state, action);
    },
    [restore.pending]: (state) => {
      state.isRestoring = true;
      state.removingRestoringError = null;
    },
    [restore.rejected]: (state, action) => {
      state.isRestoring = false;
      state.removingRestoringError = action.payload;
    },

    [deactivate.fulfilled]: (state, action) => {
      state.isUpdating = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [deactivate.pending]: (state) => {
      state.isUpdating = true;
      state.creatingUpdatingError = null;
    },
    [deactivate.rejected]: (state, action) => {
      state.isUpdating = false;
      state.creatingUpdatingError = action.payload;
    },

    [reactivate.fulfilled]: (state, action) => {
      state.isUpdating = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [reactivate.pending]: (state) => {
      state.isUpdating = true;
      state.creatingUpdatingError = null;
    },
    [reactivate.rejected]: (state, action) => {
      state.isUpdating = false;
      state.creatingUpdatingError = action.payload;
    },

    [addToFavorites.fulfilled]: entitiesAdapter.upsertOne,
    [removeFromFavorites.fulfilled]: entitiesAdapter.upsertOne,
  },
});

const { reducer } = webViewerServers;

const webViewerServersAdapterSelectors = entitiesAdapter.getSelectors(
  (state) => state[sliceName]
);

const selectedWebViewerServersSelector = createSelector(
  (state) => userConfigsSelectors.selectedItems(state, sliceName),
  (state) => webViewerServersAdapterSelectors.selectEntities(state),
  (selectedIds, entities) => selectedIds.map((id) => entities[id]),
)

export const webViewerServersSelectors = {
  ...webViewerServersAdapterSelectors,
  isFetching: (state) => state[sliceName].isFetching,
  isCreating: (state) => state[sliceName].isCreating,

  fetchingError: (state) => state[sliceName].fetchingError,
  creatingUpdatingError: (state) => state[sliceName].creatingUpdatingError,

  state: (state) => state[sliceName],
  selectedWebViewerServers: selectedWebViewerServersSelector,
};

export default reducer;



// WEBPACK FOOTER //
// ./src/store/webViewerServersSlice.js